import React, { useEffect, useState } from "react";
import { apiGet } from "../../services/api";
import "./RCTQueueWidget.css";

interface RCTCase {
  procedure_id: number;
  patient_id: string | number;
  visit_id: number | string;
  patient_name?: string;
  tooth?: string;
  phone?: string;
  consultant?: string;
  status?: string;
}

interface RCTQueueWidgetProps {
  onSelectPatient?: ((patientId: number, visitId: number) => void) | null;
  groupByPatient?: boolean;
}

type RCTDisplayCase = RCTCase & {
  key: string;
  teeth?: string[];
  consultants?: string[];
};

const splitTeeth = (raw?: string): string[] => {
  if (!raw) return [];
  return raw
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
};

export default function RCTQueueWidget({ onSelectPatient, groupByPatient = false }: RCTQueueWidgetProps) {
  const [ongoingCases, setOngoingCases] = useState<RCTCase[]>([]);
  const [loading, setLoading] = useState(false);

  const loadCases = async () => {
    setLoading(true);
    try {
      const data = await apiGet("/api/doctor/rct/ongoing") as RCTCase[];
      setOngoingCases(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load RCT queue", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
    const interval = setInterval(loadCases, 30000);
    return () => clearInterval(interval);
  }, []);

  const displayCases = React.useMemo<RCTDisplayCase[]>(() => {
    if (!groupByPatient) {
      return ongoingCases.map((item) => ({
        ...item,
        key: String(item.procedure_id),
        teeth: item.tooth ? splitTeeth(item.tooth) : []
      }));
    }

    const grouped = new Map<string, RCTDisplayCase>();
    ongoingCases.forEach((item) => {
      const key = `${item.patient_id ?? ""}`;
      const existing = grouped.get(key);
      const teeth = splitTeeth(item.tooth);
      const consultant = (item.consultant || "").trim();
      if (existing) {
        teeth.forEach((t) => existing.teeth?.push(t));
        if (consultant && existing.consultants && !existing.consultants.includes(consultant)) {
          existing.consultants.push(consultant);
        }
        if (!existing.status && item.status) existing.status = item.status;
      } else {
        grouped.set(key, {
          ...item,
          key,
          teeth: [...teeth],
          consultants: consultant ? [consultant] : []
        });
      }
    });

    return Array.from(grouped.values()).map((item) => {
      const uniqueTeeth = Array.from(new Set(item.teeth || []));
      const uniqueConsultants = Array.from(new Set(item.consultants || []));
      return {
        ...item,
        teeth: uniqueTeeth,
        consultants: uniqueConsultants
      };
    });
  }, [groupByPatient, ongoingCases]);

  return (
    <div className="rct-queue-window rct-queue-shell">
      <div className="queue-header rct-queue-header">
        <h3 className="rct-queue-title">⚡ RCT Active Queue</h3>
        <button className="refresh-btn rct-refresh" type="button" onClick={loadCases}>↻</button>
      </div>

      <div className="queue-list rct-queue-list">
        {loading && <div className="loading-row rct-loading">Loading cases...</div>}

        {!loading && displayCases.length === 0 && (
          <div className="mod-empty-state rct-empty">
            <div className="mod-empty-icon-wrapper rct-empty-icon">
              <span className="mod-empty-icon rct-empty-emoji">⚡</span>
            </div>
            <h4 className="rct-empty-title">No active RCT cases</h4>
            <p className="rct-empty-text">New RCT procedures started in Doctor module will appear here.</p>
          </div>
        )}

        {displayCases.map((item) => (
          <div
            key={item.key}
            className="mod-card rct-card"
            onClick={() => onSelectPatient?.(Number(item.patient_id), Number(item.visit_id))}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelectPatient?.(Number(item.patient_id), Number(item.visit_id));
              }
            }}
          >
            <div className="q-card-top rct-card-top">
              <span className="q-name rct-name">
                {item.patient_name}
                {(item.consultants && item.consultants.length > 0) ? (
                  <span className="rct-consultant-tag">
                    Dr. {item.consultants.join(", ")}
                  </span>
                ) : item.consultant ? (
                  <span className="rct-consultant-tag">
                    Dr. {item.consultant}
                  </span>
                ) : null}
              </span>
              <span className="q-tooth rct-tooth">
                Tooth: {item.teeth && item.teeth.length > 0 ? item.teeth.join(", ") : (item.tooth || "N/A")}
              </span>
            </div>
            <div className="q-card-bottom rct-card-bottom">
              <span className="q-phone rct-phone">📞 {item.phone}</span>
              <span className={`q-status rct-status ${item.status === "Completed" ? "is-complete" : "is-active"}`}>
                {item.status || "In Progress"}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
